import { useMutation } from '@apollo/client';
import { useTranslation } from 'next-i18next';

import { verifyApolloMutationResponse } from 'application/graphql/utils';
import { Toast } from 'ui';

import { ModeratedNewsListFiltersType } from './use-set-moderated-news-list-filters';
import GET_MODERATED_NEWS_LIST, {
  OneModeratedNewsDataType,
} from '../graphql/get-moderation-news-list';
import GET_CLIENT_COUNTS from '../graphql/get-сlient-сounts';
import MODERATE_NEWS_PUBLICATION, {
  ModerateNewsPublicationMutation,
  ModerateNewsPublicationMutationVars,
} from '../graphql/moderate-news-publication';

const useModeratePublishedNews = (filters: ModeratedNewsListFiltersType) => {
  const { t } = useTranslation(['news', 'common']);
  const [deletePublishedNewsMutation] = useMutation<
    ModerateNewsPublicationMutation,
    ModerateNewsPublicationMutationVars
  >(MODERATE_NEWS_PUBLICATION, {
    refetchQueries: [{ query: GET_CLIENT_COUNTS }],
    update(cache, { data }) {
      if (
        data?.moderateNewsPublication.__typename !== 'ModerateNewsPublication'
      ) {
        return;
      }

      const moderateNews = data.moderateNewsPublication;
      const { status, sports, sort, pageNumber, categories } = filters;
      const variables = {
        sort,
        pageNumber,
        filter: {
          categories,
          sports,
          status,
        },
      };

      const moderatedNewsList: {
        getModeratedNewsList: { news: OneModeratedNewsDataType[] };
      } | null = cache.readQuery({
        query: GET_MODERATED_NEWS_LIST,
        variables,
      });

      if (!moderatedNewsList || !moderateNews.newsId) return;

      const updatedModeratedNewsList =
        moderatedNewsList.getModeratedNewsList.news.filter(
          (news) => news.id !== moderateNews.newsId
        );

      cache.writeQuery({
        query: GET_MODERATED_NEWS_LIST,
        variables,
        data: {
          getModeratedNewsList: {
            ...moderatedNewsList.getModeratedNewsList,
            news: updatedModeratedNewsList,
            totalCount: updatedModeratedNewsList.length,
          },
        },
      });
    },
  });

  return async (variables: ModerateNewsPublicationMutationVars) => {
    try {
      const { data, errors } = await deletePublishedNewsMutation({
        variables,
      });

      const isModerateNewsSuccess = verifyApolloMutationResponse(data, errors);
      if (!isModerateNewsSuccess) return;

      if (data?.moderateNewsPublication.__typename === 'Error') {
        Toast.error(data.moderateNewsPublication.error);
        return;
      }

      if (
        data?.moderateNewsPublication.__typename === 'ModerateNewsPublication'
      ) {
        Toast.info(
          t(
            data.moderateNewsPublication.published
              ? 'news:successes:article_published'
              : 'news:successes:user_article_rejection_success'
          )
        );

        return data.moderateNewsPublication.newsId;
      }
    } catch (e: unknown) {
      Toast.error(t('common:errors:something_went_wrong'));
    }
  };
};

export default useModeratePublishedNews;
